import { InteractionStatistic, ProfilePageStructuredData } from 'lib/types/structuredData';
import { ProfileUser } from 'lib/types/users';
import { POSTING_ACCOUNT_TYPE, USER_TYPES } from 'lib/constants';
import { postingAccountToUrl } from 'lib/helpers';
import { PostServiceStats } from 'server/api/types';
import { imageAvatarUrl } from './imageUtils';

/**
 * Generates ProfilePage structured data for a user profile
 * Follows Google's schema.org ProfilePage guidelines
 * @see https://developers.google.com/search/docs/appearance/structured-data/profile-page#json-ld
 */
export function generateProfilePageStructuredData(
  profileUser: ProfileUser,
  stats?: PostServiceStats,
  totalFollowers?: number,
  baseUrl: string = 'https://gondola.com',
): ProfilePageStructuredData | undefined {
  if (!profileUser || profileUser.noIndex || profileUser.isHidden) {
    return undefined;
  }

  const profileUrl = `${baseUrl}/${profileUser.username}`;
  const avatarUrl = profileUser.avatarUrl ? imageAvatarUrl(profileUser.avatarUrl, 400) : undefined;

  // Determine if this is a Person or Organization
  const entityType = profileUser.userTypeId === USER_TYPES.company.id ? 'Organization' : 'Person';

  // Build sameAs array from owned posting accounts
  const sameAs = profileUser?.postingAccounts
    ?.filter((pa) => pa.type === POSTING_ACCOUNT_TYPE.owner)
    ?.filter((pa) => !!pa.showOnProfile)
    ?.filter((pa) => !!postingAccountToUrl(pa.network, pa.account))
    ?.map((pa) => postingAccountToUrl(pa.network, pa.account)) || [];

  if (profileUser.personalUrl) {
    sameAs.push(profileUser.personalUrl);
  }

  // Build interaction statistics
  const interactionStatistics: InteractionStatistic[] = [];

  // Add follower count if available
  if (totalFollowers && totalFollowers > 0) {
    interactionStatistics.push({
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/FollowAction',
      userInteractionCount: totalFollowers,
    });
  }

  // Add total likes if available and stats are not hidden
  if (stats && !profileUser.areStatsHidden && stats.totalLikes > 0) {
    interactionStatistics.push({
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/LikeAction',
      userInteractionCount: stats.totalLikes,
    });
  }

  const structuredData: ProfilePageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': entityType,
      name: profileUser.name,
      alternateName: profileUser.username,
      url: profileUrl,
      description: profileUser.blurb || undefined,
      sameAs: sameAs.length > 0 ? sameAs : undefined,
      dateCreated: profileUser.createdAt
        ? new Date(profileUser.createdAt).toISOString()
        : undefined,
      dateModified: profileUser.updatedAt
        ? new Date(profileUser.updatedAt).toISOString()
        : undefined,
    },
  };

  // Add dateCreated and dateModified to the main ProfilePage if available
  if (profileUser.createdAt) {
    structuredData.dateCreated = new Date(profileUser.createdAt).toISOString();
  }
  if (profileUser.updatedAt) {
    structuredData.dateModified = new Date(profileUser.updatedAt).toISOString();
  }

  // Add image if available
  if (avatarUrl) {
    structuredData.image = avatarUrl;
    structuredData.mainEntity.image = avatarUrl;
  }

  // Add interaction statistics if available
  if (interactionStatistics.length > 0) {
    structuredData.mainEntity.interactionStatistic = interactionStatistics;
  }

  return structuredData;
}
